import {
  AllocatableLoadSettings,
  AllocatableLoadSettingsPropagation,
  useCreateAllocatableLoadSettingsMutation,
  useDeleteAllocatableLoadSettingsMutation,
  usePropagateAllocatableLoadSettingsMutation,
  useUpdateAllocatableLoadSettingsMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseAllocatableLoadSettingsMutationProps = {}

export type UseAllocatableLoadSettingsMutationResult = {
  createAllocatableLoadSettings: (settings: Partial<AllocatableLoadSettings>, callback: EntityMutationCallback<AllocatableLoadSettings> | undefined) => void
  updateAllocatableLoadSettings: (settings: Partial<AllocatableLoadSettings>, callback: EntityMutationCallback<AllocatableLoadSettings> | undefined) => void
  propagateAllocatableLoadSettings: (id: string, propagation: AllocatableLoadSettingsPropagation, callback: EntityMutationCallback | undefined) => void
  deleteAllocatableLoadSettings: (id: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isPropagating?: boolean
  isDeleting?: boolean
}

export const useAllocatableLoadSettingsMutation = (props: UseAllocatableLoadSettingsMutationProps = {}): UseAllocatableLoadSettingsMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_propagating, _setPropagating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createAllocatableLoadSettings, {isLoading: isCreating}] = useCreateAllocatableLoadSettingsMutation()
  const [_updateAllocatableLoadSettings, {isLoading: isUpdating}] = useUpdateAllocatableLoadSettingsMutation()
  const [_propagateAllocatableLoadSettings, {isLoading: isPropagating}] = usePropagateAllocatableLoadSettingsMutation()
  const [_deleteAllocatableLoadSettings, {isLoading: isDeleting}] = useDeleteAllocatableLoadSettingsMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setPropagating(isPropagating)
  }, [isPropagating, _setPropagating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useAllocatableLoadSettingsUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useAllocatableLoadSettingsUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useAllocatableLoadSettingsUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useAllocatableLoadSettingsUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_propagating) {
      notifications.show({
        id: "useAllocatableLoadSettingsUpdate_propagating",
        title: t("common.status.propagating", "Propagating") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useAllocatableLoadSettingsUpdate_propagating")
    }
  }, [_propagating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useAllocatableLoadSettingsUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useAllocatableLoadSettingsUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<AllocatableLoadSettings>, callback: EntityMutationCallback<AllocatableLoadSettings> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.allocatableloadsettings", "Allocatable Load Settings") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<AllocatableLoadSettings>, callback: EntityMutationCallback<AllocatableLoadSettings> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.allocatableloadsettings", "Allocatable Load Settings") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handlePropagateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.allocatableloadsettings", "Allocatable Load Settings") + " " + t("common.status.propagated", "Propagated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.allocatableloadsettings", "Allocatable Load Settings") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, callback: EntityMutationCallback<AllocatableLoadSettings> | undefined) => {
    const promise = _deleteAllocatableLoadSettings(id).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteAllocatableLoadSettings, handleDeleteResult])

  const createAllocatableLoadSettings = useCallback((settings: Partial<AllocatableLoadSettings>, callback: EntityMutationCallback<AllocatableLoadSettings> | undefined) => {
    const promise = _createAllocatableLoadSettings(settings).unwrap()
    handleCreateResult(promise, callback)
  }, [_createAllocatableLoadSettings, handleCreateResult])

  const updateAllocatableLoadSettings = useCallback((settings: Partial<AllocatableLoadSettings>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateAllocatableLoadSettings(settings).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateAllocatableLoadSettings, handleUpdateResult])

  const propagateAllocatableLoadSettings = useCallback((id: string, propagation: AllocatableLoadSettingsPropagation, callback: EntityMutationCallback | undefined) => {
    const promise = _propagateAllocatableLoadSettings({id, propagation}).unwrap()
    handlePropagateResult(promise, callback)
  }, [_propagateAllocatableLoadSettings, handlePropagateResult])

  const deleteAllocatableLoadSettings = useCallback((id: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.allocatableloadsettings", "Allocatable Load Settings"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.allocatableloadsettings.confirm", "Are you sure you want to delete these settings?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createAllocatableLoadSettings: createAllocatableLoadSettings,
    updateAllocatableLoadSettings: updateAllocatableLoadSettings,
    propagateAllocatableLoadSettings: propagateAllocatableLoadSettings,
    deleteAllocatableLoadSettings: deleteAllocatableLoadSettings,
    isCreating: _creating,
    isUpdating: _updating,
    isPropagating: _propagating,
    isDeleting: _deleting,
  }
}
